// src/core-app/components/Matrix/MatrixChart/MatrixChart.jsx

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { fetchChartData } from '../../../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

/**
 * Longitudinal chart view of matrix data.
 * Fetches ChartJS-ready config from etl-rs and renders it.
 *
 * @component
 */
function MatrixChart({ groupBy, height }) {
  const { projectId } = useParams();
  const [chartConfig, setChartConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    fetchChartData(projectId, groupBy)
      .then((response) => {
        if (cancelled) return;
        if (response.chart) {
          setChartConfig(response.chart);
        } else {
          setError('No chart data in response');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load chart data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, groupBy]);

  if (loading) {
    return (
      <div className='matrix-chart loading'>
        <CircularProgress />
        <Typography>Loading chart data...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className='matrix-chart error'>
        <Typography color='error'>{error}</Typography>
      </div>
    );
  }

  if (!chartConfig) {
    return (
      <div className='matrix-chart empty'>
        <Typography>No time-series data available for charting.</Typography>
      </div>
    );
  }

  return (
    <div className='matrix-chart' style={{ height: height || '400px', padding: '16px' }}>
      <Line
        data={chartConfig.data}
        options={chartConfig.options}
      />
    </div>
  );
}

MatrixChart.propTypes = {
  groupBy: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MatrixChart.defaultProps = {
  groupBy: null,
  height: '400px',
};

export default MatrixChart;
